import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showApiErrorToast } from '@utils'
import { useLanguage } from '@context'

interface UpdateProfileContextsData {
  profileId: string
  meetingContextIds?: string[]
  emotionalContextIds?: string[]
  relationshipStatusId?: string | null
}

type PreviousEntries = [readonly unknown[], unknown][]

const updateProfileContexts = async ({
  profileId,
  ...payload
}: UpdateProfileContextsData): Promise<void> => {
  await api.patch(`/profile/${profileId}/contexts`, payload)
}

export const useUpdateProfileContexts = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<void, Error, UpdateProfileContextsData, { previousEntries: PreviousEntries }>({
    mutationFn: updateProfileContexts,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['profile', variables.profileId] })
      const previousEntries = queryClient.getQueriesData({
        queryKey: ['profile', variables.profileId],
      }) as PreviousEntries
      queryClient.setQueriesData({ queryKey: ['profile', variables.profileId] }, (old: unknown) =>
        old && typeof old === 'object'
          ? {
              ...(old as object),
              ...(variables.meetingContextIds !== undefined
                ? { meetingContextIds: variables.meetingContextIds }
                : {}),
              ...(variables.emotionalContextIds !== undefined
                ? { emotionalContextIds: variables.emotionalContextIds }
                : {}),
              ...(variables.relationshipStatusId !== undefined
                ? { relationshipStatusId: variables.relationshipStatusId }
                : {}),
            }
          : old,
      )
      return { previousEntries }
    },
    onError: (err, _variables, context) => {
      if (context?.previousEntries) {
        for (const [key, data] of context.previousEntries) {
          queryClient.setQueryData(key, data)
        }
      }
      showApiErrorToast(t, err)
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: ['profile', variables.profileId] })
    },
  })
}
